import { RightOutlined, WarningOutlined } from '@ant-design/icons';
import { Button, Card, Flex, List, Tag } from 'antd';
import {
  AdminAccess,
  AdminAccessListEnum,
  AdminAccessRoleEnum,
  AdminAccessStatusEnum,
  useGetAllAdminAccessQuery
} from 'api/AdminAccessApi';
import { UserStatusEnum } from 'api/UsersApi';
import ConfirmModal, { CONFIRM_DECEASED_MODAL_NAME } from 'components/Admin/ConfirmModal/ConfirmModal';
import UserAvatar from 'components/UiKit/UserAvatar/UserAvatar';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { openModal } from 'store/modalSlice';
import { useAppDispatch } from 'store/store';
import { getAdminAccessDisplayName } from 'utils/utils';
import './BeneficiaryListComponent.css';

const PAGE_SIZE = 10;

export const getAdminAccessLifecycleStatus = (adminAccess: Pick<AdminAccess, 'patientLifecycleStatus' | 'patientProfile' | 'user'>) =>
  adminAccess.patientLifecycleStatus || adminAccess.patientProfile?.lifecycleStatus || adminAccess.user?.status;

const isMemorialLikeStatus = (status?: string | null) =>
  status === UserStatusEnum.UNVERIFIED_DECEASED || status === UserStatusEnum.DECEASED || status === 'memorial';

const getPartnerFacingStatusLabel = (status?: string | null) => {
  if (status === UserStatusEnum.ACTIVE) return 'active';
  if (status === UserStatusEnum.UNVERIFIED_DECEASED) return 'pending verification';
  if (status === UserStatusEnum.DECEASED) return 'deceased';
  if (status === 'memorial') return 'memorial';

  return 'unknown';
};

const getAdminAccessEmail = (adminAccess: AdminAccess) => adminAccess.beneficiaryEmail || adminAccess.email || '—';

function BeneficiaryListComponent() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);

  const { data: beneficiariesData, isFetching } = useGetAllAdminAccessQuery({
    list: AdminAccessListEnum.Users,
    limit: PAGE_SIZE,
    filter: { status: AdminAccessStatusEnum.Accepted },
    offset: (currentPage - 1) * PAGE_SIZE
  });

  const renderStatus = (status?: string | null) => {
    if (status === UserStatusEnum.ACTIVE) {
      return (
        <Tag color="#5947cc" className="status-tag active">
          active
        </Tag>
      );
    }

    if (status === UserStatusEnum.UNVERIFIED_DECEASED || status === UserStatusEnum.DECEASED) {
      return (
        <Tag color="error" className="status-tag inactive">
          {status === UserStatusEnum.UNVERIFIED_DECEASED ? 'pending verification' : 'deceased'}
        </Tag>
      );
    }

    if (status === 'memorial') {
      return (
        <Tag color="default" className="status-tag inactive">
          memorial
        </Tag>
      );
    }

    return (
      <Tag color="#f0f0f5" className="status-tag inactive">
        {getPartnerFacingStatusLabel(status)}
      </Tag>
    );
  };

  const renderMarkDeceasedButton = (adminAccess: AdminAccess, layout: 'desktop' | 'mobile' = 'desktop') => {
    if (getAdminAccessLifecycleStatus(adminAccess) !== UserStatusEnum.ACTIVE) return null;

    return (
      <div
        className={layout === 'desktop' ? 'visible-desktop' : 'visible-mobile'}
        style={{ marginTop: layout === 'mobile' ? '24px' : '0' }}
      >
        <Button
          icon={<WarningOutlined />}
          className="mark-deceased-btn"
          onClick={(e) => {
            e.stopPropagation();
            dispatch(
              openModal({
                name: CONFIRM_DECEASED_MODAL_NAME,
                data: { adminAccessId: adminAccess.id, adminAccess }
              })
            );
          }}
        >
          Mark Deceased
        </Button>
      </div>
    );
  };

  const handleCardClick = (adminAccess: AdminAccess) => {
    if (isUserAccessible(adminAccess)) {
      navigate(`/beneficiaries/${adminAccess.id}`);
    }
  };

  const isUserAccessible = (item: AdminAccess) => {
    return item.role === AdminAccessRoleEnum.Manager && isMemorialLikeStatus(getAdminAccessLifecycleStatus(item));
  };

  return (
    <>
      <ConfirmModal />

      <div className="beneficiary-list-component">
        <List
          loading={isFetching}
          dataSource={beneficiariesData?.list || []}
          pagination={{
            current: currentPage,
            pageSize: PAGE_SIZE,
            total: beneficiariesData?.total || 0,
            onChange: (page) => setCurrentPage(page),
            align: 'center'
          }}
          renderItem={(item) => (
            <Card
              className={`beneficiary-card ${isUserAccessible(item) ? 'clickable' : ''}`}
              onClick={() => handleCardClick(item)}
            >
              <Flex align="center" justify="space-between">
                <Flex align="center" gap={16} flex={1}>
                  <UserAvatar size={56} user={item.user} fallbackType="initials" className="beneficiary-avatar" />
                  <Flex vertical={true} gap={4} flex={1}>
                    <Flex align="center" justify="space-between" gap={8}>
                      <div className="beneficiary-name">{getAdminAccessDisplayName(item, '—')}</div>
                      <div className="visible-mobile">{renderStatus(getAdminAccessLifecycleStatus(item))}</div>
                    </Flex>
                    <div className="beneficiary-email">{getAdminAccessEmail(item)}</div>
                  </Flex>
                </Flex>

                <Flex align="center" gap={16}>
                  <div className="visible-desktop">{renderStatus(getAdminAccessLifecycleStatus(item))}</div>

                  {renderMarkDeceasedButton(item, 'desktop')}

                  <div className="arrow-icon-container">
                    {isUserAccessible(item) && <RightOutlined className="arrow-icon" />}
                  </div>
                </Flex>
              </Flex>

              {renderMarkDeceasedButton(item, 'mobile')}
            </Card>
          )}
        />
      </div>
    </>
  );
}

export default BeneficiaryListComponent;
